import React from "react";
import { FeatherArrowRight } from "@subframe/core";
import { useNavigate } from "react-router-dom";
import {
  useArApAging,
  type ArApAgingCardDisplay,
} from "../../hooks/useArApAging";

function getBadgeStyles(color: string): { bg: string; text: string } {
  switch (color) {
    case "#22c55e":
      return { bg: "#f0fdf4", text: "#15803d" };
    case "#3b82f6":
      return { bg: "#eff6ff", text: "#1d4ed8" };
    case "#d97706":
      return { bg: "#fffbeb", text: "#b45309" };
    default:
      return { bg: "#fff1f2", text: "#b91c1c" };
  }
}

function AgingCard({ card }: { card: ArApAgingCardDisplay }) {
  const navigate = useNavigate();

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-stretch gap-6 rounded-lg border border-solid border-neutral-200 bg-white px-6 pt-6 pb-8 shadow-sm">
      <div className="flex flex-none flex-col items-start gap-1">
        <span className="text-heading-3 font-heading-3 text-neutral-900">
          {card.title}
        </span>
        <span className="text-caption font-caption text-neutral-500">
          {card.totalOutstanding}
        </span>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="text-caption font-caption text-neutral-500">
            {card.notOverdueAmount} and {card.overdueAmount}
          </span>
        </div>
      </div>

      <div className="flex w-full flex-none flex-col items-start gap-0.5 pt-4">
        <div className="flex h-52 w-full flex-none items-end pt-6">
          {card.buckets.map((bucket, index) => {
            const badge = getBadgeStyles(bucket.color);
            return (
              <div
                key={index}
                className="flex flex-1 flex-col items-center justify-end px-3"
              >
                <div className="relative flex flex-col items-center">
                  <div
                    className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-[calc(100%+6px)]"
                    style={{ backgroundColor: badge.bg }}
                  >
                    <div
                      className="flex items-center rounded-full px-2 py-0.5"
                      style={{ backgroundColor: badge.bg }}
                    >
                    <span
                      className="whitespace-nowrap text-caption-bold font-caption-bold"
                      style={{ color: badge.text }}
                    >
                      {bucket.amount}
                    </span>
                  </div>
                </div>
                  <div
                    className="flex h-2.5 w-2.5 flex-none flex-col items-start rounded-full mt-[2px]"
                    style={{ backgroundColor: bucket.color }}
                  />
                  <div
                    className="flex w-0.5 flex-none flex-col items-start opacity-40 mt-[2px]"
                    style={{
                      height: bucket.barHeight,
                      backgroundColor: bucket.color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex w-full items-start gap-4 mt-3">
          {card.buckets.map((bucket, index) => (
            <div
              key={index}
              className="flex flex-col items-start gap-1 overflow-hidden flex-1"
            >
              <div
                className="flex h-1 w-full flex-none items-start rounded-full"
                style={{ backgroundColor: bucket.color }}
              />
              <span className="text-body-bold font-body-bold text-default-font mt-2">
                {bucket.label}
              </span>
              <span className="text-caption font-caption text-neutral-500">
                {bucket.percent}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* {card.alertMessage && (
        <div className="flex w-full items-start gap-3 rounded-lg border border-solid border-error-200 bg-error-50 px-4 py-4">
          <FeatherAlertTriangle className="text-body font-body text-error-600 flex-none" />
          <span className="text-body-bold font-body-bold text-error-700">
            {card.alertMessage}
          </span>
        </div>
      )} */}

      {/* Fills remaining card height so CTA can sit on the bottom edge (matches taller sibling card). */}
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <div className="flex w-full flex-col items-start gap-3">
          <span className="text-caption-bold font-caption-bold text-[#6b7280] uppercase tracking-widest">
            {card.topTitle}
          </span>
          <div className="flex w-full flex-col items-start overflow-hidden rounded-lg border border-solid border-[#e5e7eb]">
            {card.topItems.length === 0 ? (
              <div className="flex w-full items-center justify-between px-4 py-3">
                <span className="text-body font-body text-neutral-500">
                  No overdue items
                </span>
              </div>
            ) : (
              card.topItems.map((item, index) => (
                <div
                  key={`${item.name}-${index}`}
                  className={`flex w-full items-center justify-between px-4 py-3 hover:bg-[#fafafa] ${
                    index < card.topItems.length - 1
                      ? "border-b border-solid border-[#f3f4f6]"
                      : ""
                  }`}
                >
                  <div className="flex flex-col items-start gap-0.5">
                    <span className="text-body font-body text-[#1c1c1c]">
                      {item.name}
                    </span>
                    <span className="text-caption font-caption text-[#9ca3af]">
                      {item.bucketLabel}
                    </span>
                  </div>
                  <span className="whitespace-nowrap text-body font-body text-[#1c1c1c]">
                    {item.amount}
                  </span>
                </div>
              ))
            )}
          </div>
          {card.othersSummary && (
            <div className="flex w-full items-center rounded-md bg-[#fafafa] px-4 py-2.5">
              <span className="text-caption font-caption text-[#6b7280]">
                {card.othersSummary}
              </span>
            </div>
          )}
        </div>
        <div className="mt-auto w-full shrink-0 pt-6">
          <div
            className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-[#1c1c1c] px-4 py-3 hover:bg-[#3D3D3D]"
            onClick={() => navigate(card.ctaRoute)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                navigate(card.ctaRoute);
              }
            }}
          >
            <span className="text-body font-body text-white">
              {card.ctaLabel}
            </span>
            <FeatherArrowRight className="text-body font-body text-white" />
          </div>
        </div>
      </div>
    </div>
  );
}

function AgingCardSkeleton() {
  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-stretch gap-6 rounded-lg border border-solid border-neutral-200 bg-white px-6 pt-6 pb-8 shadow-sm animate-pulse">
      <div className="flex w-full flex-none flex-col items-start gap-2">
        <div className="h-6 w-44 rounded bg-neutral-100" />
        <div className="h-4 w-32 rounded bg-neutral-100" />
      </div>

      <div className="flex w-full flex-none flex-col items-start gap-3 pt-4">
        <div className="flex h-48 w-full items-end gap-3">
          <div className="h-16 flex-1 rounded bg-neutral-100" />
          <div className="h-20 flex-1 rounded bg-neutral-100" />
          <div className="h-12 flex-1 rounded bg-neutral-100" />
          <div className="h-40 flex-1 rounded bg-neutral-100" />
        </div>
        <div className="flex w-full gap-4">
          <div className="h-10 flex-1 rounded bg-neutral-100" />
          <div className="h-10 flex-1 rounded bg-neutral-100" />
          <div className="h-10 flex-1 rounded bg-neutral-100" />
          <div className="h-10 flex-1 rounded bg-neutral-100" />
        </div>
      </div>

      <div className="h-14 w-full flex-none rounded bg-neutral-100" />

      <div className="flex min-h-0 w-full flex-1 flex-col">
        <div className="flex w-full flex-col items-start gap-3">
          <div className="h-4 w-44 rounded bg-neutral-100" />
          <div className="w-full rounded-lg border border-solid border-[#e5e7eb]">
            <div className="h-14 w-full border-b border-solid border-[#f3f4f6] bg-neutral-100/60" />
            <div className="h-14 w-full border-b border-solid border-[#f3f4f6] bg-neutral-100/40" />
            <div className="h-14 w-full border-b border-solid border-[#f3f4f6] bg-neutral-100/60" />
            <div className="h-14 w-full bg-neutral-100/40" />
          </div>
          <div className="h-10 w-full rounded bg-neutral-100" />
        </div>
        <div className="mt-auto w-full shrink-0 pt-6">
          <div className="h-12 w-full rounded bg-neutral-100" />
        </div>
      </div>
    </div>
  );
}

export function ArApAgingSection() {
  const data = useArApAging();

  if (data.loading) {
    return (
      <div className="grid w-full grid-cols-2 gap-8 items-stretch">
        <AgingCardSkeleton />
        <AgingCardSkeleton />
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="flex w-full rounded-lg border border-solid border-error-200 bg-error-50 px-4 py-3">
        <span className="text-body font-body text-error-700">{data.error}</span>
      </div>
    );
  }

  return (
    <div className="grid w-full grid-cols-2 gap-8 items-stretch">
      <AgingCard card={data.receivables} />
      <AgingCard card={data.payables} />
    </div>
  );
}
